import { useQuery } from "@tanstack/react-query";
import { useAppSessionScope } from "~/lib/app-session-scope";
import { withSessionQueryScope } from "~/lib/query-keys";
import { getBrowserPlatformApiClient } from "~/lib/trpc/browser-client";
import type { PlatformApiTrpcClient } from "~/lib/trpc/platform-api-adapter";

export type EditorAssetSearchOutput = Awaited<
  ReturnType<PlatformApiTrpcClient["assets"]["searchForEditor"]["query"]>
>;

export type EditorAssetSearchItem = EditorAssetSearchOutput["items"][number];

export function parseEditorAssetChainId(
  value: number | string | null | undefined,
): number | null {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value === "string" && value.trim().length > 0) {
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
}

export function usePlatformEditorAssetSearch(params: {
  chainId: string;
  enabled: boolean;
  limit: number;
  queryKey: readonly unknown[];
  searchTerm: string;
  staleTime?: number;
}) {
  const platformApi = getBrowserPlatformApiClient();
  const sessionScope = useAppSessionScope();
  const normalizedSearchTerm = params.searchTerm.trim();

  return useQuery({
    enabled:
      params.enabled &&
      params.chainId.trim().length > 0 &&
      normalizedSearchTerm.length > 0,
    queryFn: async () =>
      await platformApi.assets.searchForEditor.query({
        chainId: params.chainId,
        cursor: 0,
        limit: params.limit,
        q: normalizedSearchTerm,
      }),
    queryKey: withSessionQueryScope(params.queryKey, sessionScope),
    retry: false,
    staleTime: params.staleTime ?? 30_000,
  });
}
